import CodeEditor from '@uiw/react-textarea-code-editor';
import { Descriptions, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useMemo } from 'react';

import { Client } from '../../types/Client';
import { RequestsLog } from '../../types/RequestLog';
import { Pagination } from '../../utils/PaginationHandler';

interface RequestLogListInterfaceProps {
    wrapperStyleClass?: string;
    requestLogs: RequestsLog[];
    pagination: Pagination;
    isLoadingData: boolean;
    onPageChanges(filters: any, opts?: { isPaging: boolean }): void;
    clients: Client[];
}

const RequestLogList: React.FC<RequestLogListInterfaceProps> = (
    props: RequestLogListInterfaceProps,
): React.ReactElement => {
    const {
        wrapperStyleClass,
        requestLogs,
        pagination,
        onPageChanges: setFiltersToApply,
        isLoadingData,
        clients,
    } = props;

    const clientCache: any = {};
    const getClientProperty = useMemo(
        () => (clientId: number | null, property: keyof Client) => {
            if (clients.length === 0) return '';

            if (clientId === null) return '';
            if (!clientCache[clientId]) {
                clientCache[clientId] = clients.find((c) => c.id === clientId);
            }

            const client = clientCache[clientId];
            return client ? client[property] : '';
        },
        [clients],
    );

    const columns: ColumnsType<RequestsLog> = [
        {
            title: 'Client Name',
            dataIndex: 'clientId',
            key: 'clientId',
            render: (clientId: number | null) => getClientProperty(clientId, 'name'),
        },
        {
            title: 'Stuart client ID',
            dataIndex: 'clientId',
            key: 'clientId',
            render: (clientId: number | null) => getClientProperty(clientId, 'stuartClientId'),
        },
        {
            title: 'Request Id',
            dataIndex: 'requestId',
            key: 'requestId',
        },

        {
            title: 'URL',
            dataIndex: 'url',
            key: 'url',
        },
        {
            title: 'Method',
            dataIndex: 'method',
            key: 'method',
        },
        {
            title: 'Response Code',
            dataIndex: 'responseCode',
            key: 'responseCode',
            align: 'center',
            width: '10%',
            responsive: ['sm'],
        },

        {
            title: 'Processing Time ms',
            dataIndex: 'processingTimeMs',
            key: 'processingTimeMs',
            align: 'center',
        },
        {
            title: 'Request Time (UTC)',
            dataIndex: 'requestTime',
            key: 'requestTime',
            align: 'center',
            width: '10%',
            responsive: ['md'],
            render(requestTime: string): string {
                return `${requestTime.replace(/T|Z/g, ' ')}`;
            },
        },
        {
            title: 'Response Time (UTC)',
            dataIndex: 'responseTime',
            key: 'responseTime',
            align: 'center',
            width: '10%',
            responsive: ['md'],
            render(responseTime: string): string {
                return `${responseTime.replace(/T|Z/g, ' ')}`;
            },
        },
    ];

    return (
        <div className={wrapperStyleClass || 'px-4 pb-4 mt-3 bg-white'}>
            <Descriptions size="small">
                <Descriptions.Item label={<span className="text-black-50">N&deg; logs</span>}>
                    <span className="text-black-50">{pagination.totalItems}</span>
                </Descriptions.Item>
            </Descriptions>
            {requestLogs && (
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={requestLogs as RequestsLog[]}
                    tableLayout="fixed"
                    loading={isLoadingData}
                    pagination={{
                        pageSize: pagination.itemsPerPage,
                        current: pagination.pageNumber,
                        total: pagination.totalItems,
                        itemRender: (current, type, originalElement): string =>
                            pagination.uiControlsRenderer(current, type, originalElement),
                        showSizeChanger: false,
                        onChange: (page: number, pageSize?: number): void =>
                            setFiltersToApply(pagination.getPagingFilters(page, pageSize), { isPaging: true }),
                    }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <CodeEditor
                                language="json"
                                value={JSON.stringify(record, null, '\t')}
                                disabled
                                style={{
                                    fontSize: 12,
                                    backgroundColor: '#f5f5f5',
                                    fontFamily:
                                        'ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace',
                                }}
                            />
                        ),
                    }}
                />
            )}
        </div>
    );
};

export default RequestLogList;
